import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { useBroadcastQuery, useCancelBroadcastMutation } from '@/hooks/useBroadcasts';
import type { BroadcastStatus } from '@/api/broadcasts/broadcastsApi.types';

const STATUS_TONE: Record<BroadcastStatus, 'neutral' | 'success' | 'warning'> = {
  draft: 'neutral',
  scheduled: 'warning',
  sent: 'success',
  cancelled: 'neutral',
};

const CHANNEL_LABEL_KEY: Record<string, string> = {
  in_app: 'detail.channelInApp',
  email: 'detail.channelEmail',
  sms: 'detail.channelSms',
};

export function BroadcastDetail() {
  const { t } = useTranslation('broadcasts');
  const navigate = useNavigate();
  const { broadcastId } = useParams<{ broadcastId: string }>();
  const resolvedId = broadcastId === undefined ? '' : broadcastId;
  const broadcastQuery = useBroadcastQuery(resolvedId);
  const cancelMutation = useCancelBroadcastMutation();

  async function cancelBroadcast() {
    try {
      await cancelMutation.mutateAsync(resolvedId);
      showSuccessNotification(t('detail.cancelled'));
    } catch (cancelError) {
      const backendMessage = resolveApiErrorMessage(cancelError);
      showErrorNotification(backendMessage.length > 0 ? backendMessage : t('detail.cancelError'));
    }
  }

  if (broadcastQuery.isLoading === true) {
    return (
      <PageContainer width="full">
        <div className="py-16 flex justify-center">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  const broadcast = broadcastQuery.data;
  if (broadcast === undefined) {
    return null;
  }

  return (
    <PageContainer width="full">
      <button
        type="button"
        onClick={function back() {
          navigate(APPLICATION_ROUTES.workspaceBroadcasts);
        }}
        className="text-sm font-semibold text-zoomuted hover:text-zooindigo flex items-center gap-1 mb-4"
      >
        <Icon name="ArrowLeft" /> {t('detail.back')}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card padding="lg" className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-xl font-bold text-zooindigo">{broadcast.title}</h1>
            <Badge tone={STATUS_TONE[broadcast.status]} size="md">
              {t('status.' + broadcast.status)}
            </Badge>
          </div>
          <p className="text-sm text-zoomuted whitespace-pre-line">{broadcast.body}</p>
          {broadcast.scheduledFor !== null ? (
            <p className="text-xs text-zoowarningtext font-medium">
              {t('detail.scheduledFor', {
                date: formatIsoDateTimeToTimestamp(broadcast.scheduledFor),
              })}
            </p>
          ) : null}
          {broadcast.status === 'scheduled' ? (
            <Button
              type="button"
              variant="outline"
              size="small"
              isLoading={cancelMutation.isPending}
              onClick={cancelBroadcast}
            >
              <Icon name="X" /> {t('detail.cancel')}
            </Button>
          ) : null}
        </Card>

        <Card padding="lg" className="space-y-3">
          <h2 className="text-sm font-bold text-zooindigo">{t('detail.delivery')}</h2>
          <div className="text-xs text-zoomuted">
            {t('detail.recipients')}: {broadcast.totalRecipients}
          </div>
          <ul className="divide-y divide-zooborder">
            {broadcast.delivery.map(function renderChannel(row) {
              const labelKey = CHANNEL_LABEL_KEY[row.channel];
              return (
                <li key={row.channel} className="py-2 flex items-center justify-between text-sm">
                  <span className="font-medium text-zooindigo">
                    {labelKey === undefined ? row.channel : t(labelKey)}
                  </span>
                  <span className="flex gap-3 text-xs">
                    <span className="text-zoosuccesstext">{t('detail.sent')}: {row.sent}</span>
                    <span className="text-zooerrortext">{t('detail.failed')}: {row.failed}</span>
                    <span className="text-zoomuted">{t('detail.pending')}: {row.pending}</span>
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap gap-2 pt-1 text-[11px] text-zoomuted">
            <span>
              {t('detail.skippedNoContact')}: {broadcast.skippedNoContact}
            </span>
            <span>
              {t('detail.skippedOptedOut')}: {broadcast.skippedOptedOut}
            </span>
            {broadcast.purpose === 'marketing' ? (
              <span>
                {t('detail.skippedNoConsent')}: {broadcast.skippedNoConsent}
              </span>
            ) : null}
          </div>
        </Card>
      </div>
    </PageContainer>
  );
}
